import { useTranslation } from 'react-i18next';

import { EmployeeSaveIntents, VerificationStatus } from '@personnel/domain/types';
import type { VerificationStatusType } from '@personnel/domain/types';
import type { EmployeeSaveAction } from '@personnel/presentation/types';

/**
 * Fields the form keeps for itself. They change as a side effect of choosing a role or of loading
 * the record, so counting them as changes would light the save button up on its own.
 */
const INTERNAL_FORM_FIELDS = [
  '_credentialsEnabled',
  '_pinEnabled',
  '_isEditMode',
  '_verificationStatus',
  '_verificationRequirements',
  '_initialEmail',
  'scope',
];

/**
 * Whether the administrator changed anything, as opposed to the form changing itself.
 *
 * @param dirtyFields - The dirty map of the employee form
 */
export const hasUserChanges = (dirtyFields: Record<string, unknown>): boolean =>
  Object.keys(dirtyFields).some((key) => !INTERNAL_FORM_FIELDS.includes(key));

/**
 * What the save button says, whether it can be pressed, and the intent it reports.
 *
 * The one row that breaks with the rest of the application is editing with nothing changed and an
 * unverified email: the button is enabled and resends the link. An administrator who opens the
 * record, sees the red badge and wants to help has no other action available.
 *
 * All three intents are the same write. The intent never reaches the backend, which decides on its
 * own whether a link goes out; it names the button, its closing toast and its log attribute.
 *
 * The returned intent is always the one this label would carry — a disabled button never submits.
 *
 * @param params - Whether this is an edit, whether anything changed, and the live email state
 */
export const useEmployeeSaveIntent = ({
  isEditMode,
  hasChanges,
  verificationStatus,
}: {
  isEditMode: boolean;
  hasChanges: boolean;
  verificationStatus?: VerificationStatusType;
}): EmployeeSaveAction => {
  const { t } = useTranslation('personnel-employees');

  if (!isEditMode) {
    return {
      label: t('form.footer.saveAndInvite'),
      intent: EmployeeSaveIntents.SAVE_AND_INVITE,
      isEnabled: hasChanges,
    };
  }

  const isUnverified = verificationStatus === VerificationStatus.NOT_VERIFIED;

  if (!hasChanges) {
    return isUnverified
      ? { label: t('form.footer.resendLink'), intent: EmployeeSaveIntents.RESEND_LINK, isEnabled: true }
      : { label: t('form.footer.saveChanges'), intent: EmployeeSaveIntents.SAVE, isEnabled: false };
  }

  return isUnverified
    ? { label: t('form.footer.saveAndInvite'), intent: EmployeeSaveIntents.SAVE_AND_INVITE, isEnabled: true }
    : { label: t('form.footer.saveChanges'), intent: EmployeeSaveIntents.SAVE, isEnabled: true };
};
